import { useDatatable } from "@/stores/components/datatable";
import { Separator } from "../ui/separator";
import {
  FormDatePickerV2,
  FormInput,
  FormMultipleComboboxStatic,
  FormSelect,
  FormTimePicker
} from "../forms";
import { cn, debounce } from "@/lib/utils";
import { Form } from "../ui/form";
import { useForm } from "react-hook-form";
import { ElementType, useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Ellipsis, GripVertical, Trash2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { PutHandler, usePutData } from "@/hooks/services/use-query-hooks";

import { toastManager } from "@/lib/toast-manager";
import { isDateTimeWithClockField } from "@/lib/pps-date-time";

const maxColumns = 3;
const debounceTime = 3000;

export type ColumnType = {
  name: string;
  label: string;
  type: string;
  icon: ElementType;
  placeholder?: string;
  formatDate?: string;
  options?: { label: string; value: string; raw?: any }[] | undefined;
  initialValue?:
    | ((func: (value: any) => void, column: ColumnType, selection: any) => void)
    | undefined;
};

interface FieldColumnType {
  current: ColumnType[];
  remaining: ColumnType[];
}

function DynamicDataTableEditingColumn({
  columns,
  api,
  refetch,
  onHandleDelete
}: {
  columns: ColumnType[];
  api: () => PutHandler<any, any>;
  refetch?: () => void;
  onHandleDelete?: (value: any) => void;
}) {
  const form = useForm();
  const { multipleRowSelections, setRowSelectionTable } = useDatatable();
  const { mutate, isPending } = usePutData(api());

  const flattenSubJobOrder = (array: any[] = []) => {
    const newArray: any[] = [];

    if (array.length > 0) {
      array.forEach((item) => {
        newArray.push(item);

        if (Array.isArray(item.subJobOrders)) {
          newArray.push(...flattenSubJobOrder(item.subJobOrders));
        }
      });
    }

    return newArray;
  };

  // this only for triggered
  // why using this instead watch or onchange only, cause it doesnt give a behaviour that we expected
  // so we decide to rerender this component by triggering useEffect
  const [changes, setChanges] = useState("");

  const [fieldColumns, setFieldColumns] = useState<FieldColumnType>({
    current: [],
    remaining: []
  });

  useEffect(() => {
    setFieldColumns({
      current: columns.slice(0, maxColumns),
      remaining: columns.slice(maxColumns, columns.length)
    });
  }, [columns]);

  useEffect(() => {
    //  reset initial value each change the selection
    form.reset();
  }, [multipleRowSelections]);

  useEffect(() => {
    const body: any[] = [];

    const hasValue = Object.entries(form.getValues()).every(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([_, value]) => !!value
    );

    if (hasValue) {
      flattenSubJobOrder(multipleRowSelections).forEach((item) => {
        body.push({
          id: item.original.id,
          ...form.getValues()
        });
      });

      if (body.length > 0) {
        mutate(body, {
          onSuccess: () => {
            toastManager.showSuccess(
              "Column edited successfully",
              `Column ${Object.entries(form.getValues())
                .map(([key]) => key)
                .join(",")} has been updated successfully.`
            );

            if (refetch) {
              refetch();
              // reset the selection so it will rerender the component
              setRowSelectionTable({});
              form.reset();
            }
          },
          onError: (error) => {
            const errorDetail = Array.isArray(error?.message)
              ? error.message.join("\n")
              : (error?.message ?? "");

            toastManager.showError(
              "Error Update Fields",
              `There was an issue editing the field.${
                errorDetail ? ` ${errorDetail}` : ""
              }`
            );
          }
        });
      }
    }
  }, [changes]);

  const onHandleDeleteItems = (rows: any) => {
    if (onHandleDelete) {
      onHandleDelete(rows);
    }
  };

  return (
    <Form {...form}>
      {multipleRowSelections.length > 0 && (
        <form
          onSubmit={(e) => e.preventDefault()}
          className="aero:flex aero:h-12 aero:border aero:border-gray-200 aero:p-2 aero:!rounded-md aero:items-center aero:space-x-4 aero:!text-sm"
        >
          <span className="aero:!text-blue-500 aero:!font-medium">
            {multipleRowSelections.length} selected
          </span>

          {fieldColumns.current.map((column: any) => {
            return (
              <div key={column.label}>
                <Separator orientation="vertical" />
                <Popover>
                  <div className="aero:flex aero:items-center aero:gap-1">
                    <PopoverTrigger>
                      <Button
                        design={"text"}
                        className={cn(
                          "aero:flex aero:items-center aero:gap-2py-1"
                          // form.getValues(column.name) && 'border-blue-500 text-blue-700',
                        )}
                        onClick={() => {
                          if (multipleRowSelections.length > 0) {
                            if (column.initialValue) {
                              column.initialValue(
                                form.setValue,
                                column,
                                multipleRowSelections[0]
                              );
                            } else {
                              form.setValue(
                                `${column.name}`,
                                multipleRowSelections[0].original[column.name]
                              );
                            }
                          }
                        }}
                      >
                        <column.icon className="aero:h-4 aero:w-4" />
                        <div>{column.label}</div>
                      </Button>
                    </PopoverTrigger>
                  </div>
                  <PopoverContent>
                    <FormFields
                      item={column}
                      control={form.control}
                      setCallback={setChanges}
                      isPending={isPending}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            );
          })}

          <Separator orientation="vertical" />

          <Button
            type="button"
            onClick={() => onHandleDeleteItems(multipleRowSelections)}
            design={"text"}
            size={"iconMd"}
          >
            <Trash2 className="aero:h-4 aero:w-4 aero:!text-destructive" />
          </Button>

          {fieldColumns.remaining.length > 0 && (
            <Popover>
              <PopoverTrigger>
                <Button type="button" design={"outline"} size={"iconMd"}>
                  <Ellipsis className="aero:h-4 aero:w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                {fieldColumns.remaining.map((item, idx) => (
                  <div
                    key={idx}
                    className="aero:flex aero:flex-col aero:gap-2 aero:hover:bg-muted/90 aero:!rounded-md aero:px-2 aero:py-1"
                  >
                    {idx > 0 && <Separator />}

                    <Tooltip>
                      <TooltipTrigger>
                        <div
                          key={idx}
                          className="aero:flex aero:items-center aero:gap-1"
                        >
                          <GripVertical className="aero:h-4 aero:w-4 aero:!text-muted-foreground" />
                          <div className="aero:inline-flex aero:gap-2 aero:items-center">
                            <item.icon className="aero:h-4 aero:w-4" />
                            <div className="aero:!text-sm">{item.label}</div>
                          </div>
                        </div>
                      </TooltipTrigger>

                      <TooltipContent className="aero:bg-secondary aero:!text-gray-700">
                        This field is not ready yet.
                      </TooltipContent>
                    </Tooltip>
                  </div>
                ))}
              </PopoverContent>
            </Popover>
          )}
        </form>
      )}
    </Form>
  );
}

const FormFields = ({ item, control, setCallback, isPending }: any) => {
  switch (item.type) {
    case "select":
      return (
        <FormSelect
          name={item.name}
          placeholder={`Select ${item.label}`}
          onValueChange={debounce((value: string) => {
            setCallback(value);
          }, debounceTime)}
          options={item.options || []}
          control={control}
          disabled={isPending}
        />
      );
    case "input":
      return (
        <FormInput
          name={item.name}
          onValueChange={debounce((value: string | number) => {
            setCallback(value || "");
          }, debounceTime)}
          control={control}
          placeholder={`Input ${item.label}`}
          disabled={isPending}
        />
      );

    case "date": {
      const isDateTimeWithClock = isDateTimeWithClockField(
        item.name,
        item.label
      );

      return (
        <FormDatePickerV2
          name={item.name}
          onValueChange={debounce((value: string) => {
            setCallback(value);
          }, debounceTime)}
          control={control}
          showTime={isDateTimeWithClock}
          placeholder={
            isDateTimeWithClock
              ? `Date and time ${item.label}`
              : `Date ${item.label}`
          }
          disabled={isPending}
        />
      );
    }
    case "time":
      return (
        <FormTimePicker
          control={control}
          onValueChange={debounce((value: string) => {
            setCallback(value);
          }, debounceTime)}
          name={item.name}
          label={item.label}
          description={`Choose ${item.label}`}
          disabled={isPending}
        />
      );
    case "multiple-combobox":
      return (
        <FormMultipleComboboxStatic
          name={item.name}
          control={control}
          options={item.options}
          placeholder={item.placeholder}
          onSelectItem={debounce((value: any) => {
            setCallback(value);
          }, debounceTime)}
          disabled={isPending}
        />
      );
    default:
      return (
        <FormInput
          name={item.name}
          onValueChange={debounce((value: string | number) => {
            setCallback(value);
          }, debounceTime)}
          control={control}
          placeholder={`Input ${item.label}`}
          disabled={isPending}
        />
      );
  }
};

export { DynamicDataTableEditingColumn };
